import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Header,
  Heading,
  Text,
  ResponsiveContext,
  Layer,
  Select,
} from "grommet";
import { AuthContext } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_URL from "../../config/config";
import { useSearchParams } from "react-router-dom";
import { Info } from "grommet-icons";

export const LoginForm = ({ setLoading }) => {
  const [searchParams] = useSearchParams();

  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const size = React.useContext(ResponsiveContext);
  const { login, user, authLoading } = useContext(AuthContext);
  const redirectedRef = useRef(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const [userId, setUserId] = useState("");
  const [userTableId, setUserTableId] = useState("");
  const [openRedirectModal, setOpenRedirectModal] = useState(false);

  const navigate = useNavigate();

  const fetchData = async () => {
    redirectedRef.current = true;
    try {
      const redirectResponse = await axios.get(`${API_URL}/user/redirect`, {
        withCredentials: true,
      });

      const { url, user: redirectUser } = redirectResponse.data;

      if (!redirectUser || typeof redirectUser !== "object") {
        console.error("Error redirecting: no valid user in redirect response");
        redirectedRef.current = false;
        setLoading(false);
        return;
      }

      login(redirectUser);
      setUserId(redirectUser.user_id);
      setUserTableId(redirectUser.user_table_id ?? null);

      setRedirectUrl(url);
      if (url === "master-access") {
        setOpenRedirectModal(true);
        setLoading(false);
      } else {
        navigate(url);
      }
    } catch (error) {
      console.error("Error redirecting:", error);
      setLoading(false);
    }
  };

  // Send a request to the OKTA backend to check the auth status
  const oktaCallback = async () => {
    try {
      const status = await axios.get(
        `${API_URL}/auth/callback?code=${code}&state=${state}`,
        { withCredentials: true }
      );
      if (status.data.isAuthenticated) {
        fetchData();
        return true;
      }
    } catch (error) {
      // Callback auth failed — fall through to session check
      console.error("Error during OKTA callback authentication:", error);
    }
    return false;
  };

  const getAuthStatus = async () => {
    try {
      const status = await axios.get(`${API_URL}/user/status`, { withCredentials: true });
      if (status.data.isAuthenticated) {
        fetchData();
        return true;
      }
    } catch (error) {
      // Not authenticated via session
      console.error("Error checking authentication status:", error);

    }
    return false;
  };

  // Navigate based on JWT claims — no Okta session required.
  const navigateByRole = (u) => {
    if (u.role === "master_admin") {
      setUserId(u.user_id);
      setUserTableId(u.user_table_id ?? null);
      setOpenRedirectModal(true);
      setLoading(false);
    } else if (u.role === "manager") {
      navigate(`/${u.user_id}/manager/dashboard`);
    } else if (u.role === "admin") {
      navigate("/admin/dashboard");
    } else if (u.role === "pursuit_access") {
      navigate("/pursuit/dashboard");
    } else {
      // Returning user with valid cookie — send straight to home
      const uid = u.user_table_id ?? u.user_id;
      navigate(`/add-proficiency/${uid}`);
    }
  };

  // If the cookie check in AuthContext already resolved a valid user, skip the
  // Okta session round-trip (/user/redirect requires isAuthenticated) and
  // navigate directly. This is the primary fix for "login fails sometimes".
  useEffect(() => {
    if (!authLoading && user && !redirectedRef.current) {
      redirectedRef.current = true;
      navigateByRole(user);
    }
  }, [authLoading, user]);

  useEffect(() => {
    const checkAuth = async () => {
      // Step 1: If redirected from OKTA — clear the code/state from the URL
      // immediately (code/state are captured in the closure above, still usable)
      if (code && state) {
        navigate("/login", { replace: true });
        const callbackAuth = await oktaCallback();
        if (callbackAuth) return;
      }

      // Step 3: fallback → check session
      const statusAuth = await getAuthStatus();

      if (!statusAuth) {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const handleLogin = () => {
    window.location.href = `${API_URL}/auth/okta/login`; // Redirect to backend login route
  };

  const handleRedirectChoice = (role) => {
    setOpenRedirectModal(false);
    if (role === "Manager") {
      sessionStorage.setItem("portalView", "manager");
      navigate(`/${userId}/manager/dashboard`);
    } else {
      sessionStorage.setItem("portalView", "user");
      if (role === "Pursuit Access") navigate("/pursuit/dashboard");
      else if (role === "Portfolio Management") navigate("/admin/dashboard");
      else if (role === "User") navigate(`/add-proficiency/${userTableId}/`);
    }
  };

  return (
    <Box width="medium">
      <Header
        direction="column"
        align="start"
        gap="xxsmall"
        pad={{ horizontal: "xxsmall" }}
      >
        <Heading level={2} margin="none">
          Sign In or Sign Up
        </Heading>
        <Text>for your HPE Digital Skill Advisor account</Text>
      </Header>
      <Box pad={{ horizontal: "xxsmall" }}>
        <Box
          align={!["xsmall", "small"].includes(size) ? "start" : undefined}
          margin={{ top: "small", bottom: "small" }}
        >
          <Button
            label="Click here"
            primary
            type="button"
            onClick={handleLogin}
            margin={{ bottom: "small" }}
          />
        </Box>
      </Box>

      {/* Full-screen Redirect Modal */}
      {openRedirectModal && (
        <Layer
          align="center"
          justify="center"
          position="center"
          onEsc={() => setOpenRedirectModal(false)}
          onClickOutside={() => setOpenRedirectModal(false)}
        >
          <Box
            align="center"
            justify="center"
            pad="medium"
            background="light-1" // Add a background color for the modal
            round="small" // Rounded corners for the modal
            elevation="large" // Add shadow to the modal for a pop effect
            width="large" // Set a maximum width for the modal
            style={{ textAlign: "center" }} // Center-align text
          >
            <Heading level={2} margin="none" color="dark-1">
              Which portal would you like to access?
            </Heading>
            <Box
              pad="medium"
              background="light-2"
              round="small"
              flex={{ grow: 1, shrink: 1 }} // Flexible width
              border={{ color: "brand", size: "small" }}
              width="full"
              margin={{ top: "small" }} // Add margin to the top
            >
              <Box direction="row" align="center" gap="small">
                <Info size="large" color="brand" />

                <Text size="medium" color="dark-3">
                  You have been granted Pursuit access - select 'Pursuit Access'
                  to go to the Pursuit dashboard. To reach the Portfolio portal,
                  choose 'Portfolio Management'. Alternatively, you may log in
                  to the Manager or User portals, depending on the other roles
                  assigned to you.
                </Text>
              </Box>

              {/* Body text */}
            </Box>

            <Box
              direction="row"
              gap="medium"
              margin={{ top: "medium" }}
              align="center"
              justify="center"
              width="medium"
            >
              <Select
                options={
                  userId && userTableId
                    ? ["Pursuit Access", "Manager", "User", "Portfolio Management"]
                    : ["Pursuit Access", "User", "Portfolio Management"]
                }
                value={""}
                onChange={({ option }) => handleRedirectChoice(option)}
                placeholder="Select Portal"
                size="medium"
                margin={{ top: "small" }}
              />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
};
